import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Eye, EyeOff, LockKeyhole } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { isMenuAdmin } from "../../lib/menuService";
import buscavidaText from "../../assets/images/busca-vida-text.png";
import "./Admin.css";
import Seo from "../../components/Seo";

export default function AdminLogin() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSession, setHasSession] = useState(false);
    const [isChecking, setIsChecking] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        let isMounted = true;

        supabase.auth.getSession().then(({ data }) => {
            if (isMounted) {
                setHasSession(Boolean(data.session));
                setIsChecking(false);
            }
        });

        return () => {
            isMounted = false;
        };
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrorMessage("");
        setIsLoading(true);

        const { error } = await supabase.auth.signInWithPassword({
            email: email.trim(),
            password,
        });

        if (error) {
            setErrorMessage("Email o password non corrette.");
            setIsLoading(false);
            return;
        }

        try {
            const allowed = await isMenuAdmin();

            if (!allowed) {
                await supabase.auth.signOut();
                setErrorMessage("Questo account non è autorizzato.");
                setIsLoading(false);
                return;
            }

            navigate("/admin", { replace: true });
        } catch {
            await supabase.auth.signOut();
            setErrorMessage("Impossibile verificare l’accesso. Riprova.");
            setIsLoading(false);
        }
    };

    if (isChecking) {
        return <div className="admin-loading">Verifica accesso…</div>;
    }

    if (hasSession) {
        return <Navigate to="/admin" replace />;
    }

    return (
        <main className="admin-auth-page">
            <Seo
                title="Accesso amministratore | Busca Vida"
                description="Area riservata agli amministratori di Busca Vida."
                path="/admin/login"
                noIndex
            />
            <section className="admin-login-card">
                <a href="/" className="admin-brand" aria-label="Torna al sito">
                    <img src={buscavidaText} alt="Busca Vida" />
                </a>

                <div className="admin-login-icon">
                    <LockKeyhole size={24} />
                </div>
                <p className="admin-eyebrow">Area riservata</p>
                <h1>Gestione menu</h1>
                <p className="admin-login-copy">
                    Accedi con l’account amministratore per aggiornare i piatti.
                </p>

                <form onSubmit={handleSubmit} className="admin-login-form">
                    <label>
                        Email
                        <input
                            type="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            autoComplete="username"
                            required
                        />
                    </label>

                    <label>
                        Password
                        <span className="admin-password-field">
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                autoComplete="current-password"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((current) => !current)}
                                aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                            >
                                {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
                            </button>
                        </span>
                    </label>

                    {errorMessage && (
                        <p className="admin-form-error" role="alert">{errorMessage}</p>
                    )}

                    <button className="admin-primary-button" type="submit" disabled={isLoading}>
                        {isLoading ? "Accesso…" : "Accedi"}
                    </button>
                </form>

                <a className="admin-back-link" href="/">← Torna al sito</a>
            </section>
        </main>
    );
}
